<template>
  <div id="ticket">
    <Loader :active="loading" :full="false"></Loader>
    <div class="ticket-content" v-if="toggle && !loading">
      <div class="columns">
        <div class="column">
          <div class="level ticket-heading-row">
            <div class="level-left is-block">
              <div class="level-item is-justify-content-flex-start mb-2">
                <h4 class="title is-4 has-text-primary is-uppercase">
                  Consulta
                </h4>
                <h4 class="title is-4 has-text-dark ml-3">
                  #{{ ticketId(ticket.id) }}
                </h4>
                <b-tag
                  :type="`is-${ticket.color_status}`"
                  class="ticket-status-mobile is-uppercase has-text-weight-bold"
                >
                  {{ ticket.statuses.status }}
                </b-tag>
              </div>
              <div class="level ticket-meta-row">
                <div class="level-left">
                  <div class="level-item">
                    <b-tooltip
                      class="crm-tooltip ticket-meta-tooltip"
                      label="Procedencia"
                      type="is-dark"
                      position="is-bottom"
                      :square="true"
                    >
                      <div class="tags has-addons">
                        <span
                          class="tag is-uppercase has-text-weight-bold"
                          :type="ticket.origin.type"
                          >{{ ticket.origin.name }}</span
                        >
                        <span class="tag" :type="ticket.origin.type">
                          <b-icon
                            :pack="ticket.origin.pack"
                            :icon="ticket.origin.icon"
                            size="is-small"
                          >
                          </b-icon>
                        </span>
                      </div>
                    </b-tooltip>
                  </div>
                  <div class="level-item">
                    <span class="has-text-grey">|</span>
                  </div>

                  <div
                    v-for="(user, index) of ticket.delegated"
                    :key="index"
                    class="level-item"
                  >
                    <b-tooltip
                      class="crm-tooltip ticket-meta-tooltip"
                      :label="`Delegado a: ${user.name}`"
                      type="is-dark"
                      position="is-bottom"
                      :square="true"
                    >
                      <div class="media">
                        <div class="media-left mr-2">
                          <figure class="image is-24x24 m-0">
                            <img
                              class="is-rounded"
                              style="height: 100%"
                              :src="`${
                                user.avatar ? user.avatar : $avatarDefault
                              }`"
                            />
                          </figure>
                        </div>
                      </div>
                    </b-tooltip>
                  </div>
                </div>
              </div>
              <b-collapse
                class="ticket-contact-collapse"
                :open="false"
                animation="slide"
                position="is-top"
                aria-id="contentIdForA11y1"
              >
                <template #trigger="props">
                  <div class="is-flex ticket-contact-trigger">
                    <h4 class="title is-4 has-text-primary mb-0 mr-3">
                      {{ ticket.contact.name }}
                    </h4>
                    <a class="has-text-grey" aria-controls="contentIdForA11y1">
                      {{ !props.open ? "ver más" : "ver menos" }}
                      <b-icon
                        pack="fas"
                        :icon="!props.open ? 'sort-down' : 'sort-up'"
                      ></b-icon>
                    </a>
                  </div>
                </template>
                <div class="ticket-contact-pills">
                  <span v-if="ticket.contact.dni" class="ticket-contact-pill">
                    <i class="fas fa-id-card" aria-hidden="true"></i>
                    DNI: {{ ticket.contact.dni }}
                  </span>
                  <span v-if="ticket.contact.cuil" class="ticket-contact-pill">
                    <i class="fas fa-address-card" aria-hidden="true"></i>
                    CUIL: {{ ticket.contact.cuil }}
                  </span>
                  <a
                    v-if="ticket.contact.email"
                    class="ticket-contact-pill"
                    :href="`mailto:${ticket.contact.email}`"
                  >
                    <i class="fas fa-envelope" aria-hidden="true"></i>
                    {{ ticket.contact.email }}
                  </a>
                  <a
                    v-if="ticket.contact.phone"
                    class="ticket-contact-pill"
                    :href="`tel:${ticket.contact.phone}`"
                  >
                    <i class="fas fa-phone" aria-hidden="true"></i>
                    {{ ticket.contact.phone }}
                  </a>
                  <a
                    v-if="ticket.contact.celphone"
                    class="ticket-contact-pill"
                    :href="whatsappLink(ticket.contact.celphone)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="fab fa-whatsapp" aria-hidden="true"></i>
                    {{ ticket.contact.celphone }}
                  </a>
                  <span v-if="contactLocation" class="ticket-contact-pill">
                    <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                    {{ contactLocation }}
                  </span>
                  <a
                    v-if="ticket.contact.id && $hasPermission('contactos.editar')"
                    class="ticket-contact-pill ticket-contact-pill--primary"
                    :href="`/contacts/editar/${ticket.contact.id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="fas fa-user" aria-hidden="true"></i>
                    VER MÁS INFO
                  </a>
                </div>
              </b-collapse>
            </div>
            <div class="level-right is-block ticket-header-actions">
              <div class="buttons mb-0">
                <b-tooltip
                  v-if="ticket.statuses.status == 'Abierto'"
                  class="mr-2 crm-tooltip"
                  :label="
                    ticket.is_urgent ? 'Quitar urgente' : 'Marcar urgente'
                  "
                  type="is-dark"
                  position="is-top"
                  :square="true"
                >
                  <b-button
                    :class="ticket.is_urgent ? 'active' : ''"
                    class="is-circle ticket-header-action"
                    @click="confirmUrgentTicket"
                    type="is-danger"
                    icon-pack="fas"
                    icon-left="exclamation"
                  >
                  </b-button>
                </b-tooltip>

                <b-tooltip
                  class="crm-tooltip"
                  label="Historial"
                  type="is-dark"
                  position="is-top"
                  :square="true"
                >
                  <b-button
                    class="is-circle ticket-header-action"
                    @click.prevent="fetchTimeLine()"
                    type="is-primary"
                    icon-pack="fas"
                    icon-left="history"
                  >
                  </b-button>
                </b-tooltip>
              </div>

              <b-tag
                :type="`is-${ticket.color_status}`"
                class="ticket-status-desktop is-uppercase has-text-weight-bold"
              >
                {{ ticket.statuses.status }}
              </b-tag>
            </div>
          </div>
        </div>
      </div>
      <div class="columns" v-if="ticket.delegate.active">
        <div class="column">
          <!-- Delegate Section -->
          <section class="delegate" v-if="ticket.delegate.active">
            <div class="is-flex is-justify-content-space-between">
              <p class="title is-5 is-uppercase has-text-grey-light">
                Delegar consulta
              </p>
              <button
                type="button"
                class="delete"
                @click="resetDelegateForm(true)"
              ></button>
            </div>

            <!-- Departaments -->
            <div v-if="departaments.active" class="departaments">
              <b-field
                label="Departamento*"
                message="Seleccionar el departamento donde será delegada la consulta."
              >
                <multiselect
                  v-model="ticket.delegate.departament"
                  :options="departaments.data"
                  :group-select="false"
                  placeholder="Seleccionar"
                  track-by="id"
                  label="name"
                  @select="onDepartamentSelect"
                  selectLabel="Presione enter para seleccionar"
                  selectedLabel="Seleccionado"
                  deselectLabel="Presione Enter para deshabilitar"
                >
                  <span slot="noResult">No se encontraron resultados.</span>
                </multiselect>
              </b-field>
            </div>

            <!-- Delegations -->
            <div
              v-if="delegations.active && ticket.delegate.departament"
              class="departaments"
            >
              <b-field
                label="Delegaciones*"
                message="Seleccionar la delegacion."
              >
                <multiselect
                  v-model="ticket.delegate.delegation"
                  :options="delegations.data"
                  :group-select="false"
                  placeholder="Seleccionar"
                  group-values="items"
                  group-label="type"
                  select-label=""
                  deselect-label="Click para remover"
                  track-by="id"
                  label="name"
                  @select="onDelegationSelect"
                  selectLabel="Presione enter para seleccionar"
                  selectedLabel="Seleccionado"
                  deselectLabel="Presione Enter para deshabilitar"
                >
                  <span slot="noResult">No se encontraron resultados.</span>
                </multiselect>
              </b-field>
            </div>

            <!-- Agents -->
            <Loader :active="agents.loading" :full="false"></Loader>
            <div v-if="agents.active" class="agents">
              <label class="label">Agentes*</label>
              <ul class="menu-list ml-0">
                <li v-for="agent in agents.data" :key="agent.id">
                  <b-field>
                    <b-checkbox
                      v-model="ticket.delegate.agents"
                      :native-value="agent.id"
                      :value="true"
                    >
                      {{ agent.name }} - {{ agent.position }}
                    </b-checkbox>
                  </b-field>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
      <div class="columns" v-else>
        <div class="column">
          <!-- Message Section -->
          <section class="messages">
            <ul v-chat-scroll>
              <li v-for="(message, index) in ticket.messages" :key="index">
                <!-- Message Received -->
                <div
                  v-if="message.type == 'received' && message.files.length > 0"
                >
                  <div
                    class="control mr-2 mb-2"
                    v-for="(file, index) in message.files"
                    :key="index"
                  >
                    <b-field>
                      <a :href="file.path" download>
                        <b-tag aria-close-label="Close tag">
                          <div class="media is-align-items-center">
                            <div class="media-left">
                              <b-icon
                                pack="fas"
                                :icon="
                                  file.type == 'image/jpg' ||
                                  file.type == 'image/png'
                                    ? 'image'
                                    : 'paperclip'
                                "
                              >
                              </b-icon>
                            </div>
                            <div class="media-content">
                              <p>{{ urlShort(file.path) }}</p>
                            </div>
                          </div>
                        </b-tag>
                      </a>
                    </b-field>
                  </div>
                </div>
                <div
                  v-if="message.type == 'received' && message.content != ''"
                  class="message-box"
                >
                  <!-- Mensaje del asociado -->

                  <b-message>
                    <div class="custom-message-content">
                      <pre
                        style="
                          white-space: pre-line;
                          font-family: GothamRounded, sans-serif;
                          color: black;
                          background-color: #f5f5f5;
                        "
                        >{{ message.content }}</pre
                      >
                      <small class="has-text-grey">{{ message.date }} hs</small>
                    </div>
                  </b-message>

                  <!--
                  <div class="avatar">
                    <figure class="image is-48x48 ml-3">
                      <img
                        class="is-rounded"
                        src="https://bulma.io/images/placeholders/48x48.png"
                      />
                    </figure>
                  </div>
                     -->
                </div>
                <!-- Message Sended -->
                <div
                  v-if="message.type == 'sended' && message.files.length > 0"
                >
                  <div
                    class="control mr-2 mb-2"
                    v-for="(file, index) in message.files"
                    :key="index"
                  >
                    <b-field>
                      <a :href="file.path" download>
                        <b-tag
                          style="margin-left: auto"
                          aria-close-label="Close tag"
                        >
                          <div class="media is-align-items-center">
                            <div class="media-left">
                              <b-icon
                                pack="fas"
                                :icon="
                                  file.type == 'image/jpg' ||
                                  file.type == 'image/png'
                                    ? 'image'
                                    : 'paperclip'
                                "
                              >
                              </b-icon>
                            </div>
                            <div class="media-content">
                              <p>{{ urlShort(file.path) }}</p>
                            </div>
                          </div>
                        </b-tag>
                      </a>
                    </b-field>
                  </div>
                </div>
                <div
                  v-if="message.type == 'sended' && message.content != ''"
                  class="message-box me"
                >
                  <div class="avatar">
                    <figure class="image is-48x48 mr-3">
                      <img
                        class="is-rounded"
                        :src="messageAvatar(message)"
                        :alt="message.user || 'Agente'"
                      />
                    </figure>
                  </div>
                  <b-message>
                    <p class="has-text-white mb-3">{{ message.content }}</p>
                    <small class="has-text-white is-size-7"
                      >{{ message.date }} hs</small
                    ><br />
                    <small class="has-text-white is-size-7"
                      >Enviado por: {{ message.user }}
                    </small>
                  </b-message>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>
      <nav class="navbar">
        <div v-if="ticket.delegate.active">
          <div class="buttons is-centered">
            <b-button type="is-primary" @click="delegateAgent">
              Guardar
            </b-button>

            <b-button type="is-light" @click="resetDelegateForm(true)">
              Cancelar
            </b-button>
          </div>
        </div>

        <div v-else>
          <div
            class="field has-addons is-flex-wrap-wrap"
            v-if="ticket.answer.files.length > 0"
          >
            <div
              class="control mr-2 mb-2"
              v-for="(file, index) in ticket.answer.files"
              :key="index"
            >
              <b-field>
                <b-tag
                  closable
                  aria-close-label="Close tag"
                  @close="removeFile(index)"
                >
                  <div class="media is-align-items-center">
                    <div class="media-left">
                      <b-icon
                        pack="fas"
                        :icon="
                          file.type == 'image/jpg' || file.type == 'image/png'
                            ? 'image'
                            : 'paperclip'
                        "
                      />
                    </div>
                    <div class="media-content">
                      <p>{{ file.name }}</p>
                    </div>
                  </div>
                </b-tag>
              </b-field>
            </div>
          </div>

          <div
            class="field has-addons"
            v-if="ticket.statuses.status == 'Abierto' && ticket.answer.active"
          >
            <div class="control is-expanded">
              <input
                class="input"
                v-model="ticket.answer.message"
                type="text"
                placeholder="Escribe tu mensaje..."
              />
            </div>

            <div class="control">
              <b-field class="file is-white">
                <b-upload
                  class="file-label"
                  style="opacity: 1"
                  multiple
                  v-model="ticket.answer.files"
                >
                  <span class="button">
                    <b-icon pack="fas" icon="paperclip" />
                  </span>
                </b-upload>
              </b-field>
            </div>

            <div class="control">
              <b-button
                @click="answerTicket"
                icon-pack="fas"
                icon-right="paper-plane"
              />
            </div>
          </div>

          <div class="buttons is-centered">
            <b-button
              v-if="ticket.statuses.status == 'No atendido'"
              @click="openTicket"
              type="is-warning"
              outlined
            >
              Atender consulta
            </b-button>

            <b-button
              v-if="
                ticket.departament.name == 'Ventas' &&
                ticket.statuses.status == 'Abierto' &&
                !ticket.lead
              "
              @click="createLead"
              type="is-warning"
              outlined
            >
              Crear lead
            </b-button>

            <b-button
              v-if="ticket.statuses.status == 'Cerrado'"
              @click="openTicket"
              type="is-warning"
              outlined
            >
              Reabrir consulta
            </b-button>

            <b-button
              v-if="ticket.statuses.status == 'Abierto'"
              @click="closeTicket"
              type="is-primary"
            >
              Finalizar consulta
            </b-button>

            <b-button
              v-if="ticket.statuses.status == 'Abierto'"
              @click="openDelegate"
              type="is-light"
            >
              Delegar
            </b-button>
          </div>
        </div>
      </nav>
      <TimeLine
        :loading="timeline.loading"
        :empty="timeline.empty"
        :data="timeline.data"
      />
    </div>
  </div>
</template>

<script>
import TimeLine from "./Ticket-timeline.vue";
import { mapState } from "vuex";
import { alertSuccess, alertFailed, alertInfo, fillString } from "@/helpers";
import Loader from "./Loader.vue";
import Multiselect from "vue-multiselect";
export default {
  name: "Ticket",
  components: {
    TimeLine,
    Loader,
    Multiselect,
  },
  data() {
    return {
      loading: false,
      departaments: {
        data: [],
        active: true,
        disabled: false,
        loading: false,
      },
      delegations: {
        data: [],
        active: false,
        disabled: false,
        loading: false,
      },
      agents: {
        data: [],
        active: false,
        loading: false,
      },
      ticket: {
        id: null,
        statuses: {
          status: "",
        },
        delegated: [],
        agent: {
          id: null,
          name: "",
          lastname: "",
          avatar: null,
        },
        departament: {
          id: null,
          name: "",
        },
        contact: {
          id: null,
          name: "",
          lastname: "",
          email: "",
          phone: "",
          celphone: "",
          dni: "",
          cuil: "",
          city: "",
          province: "",
        },
        messages: [],
        files: [],
        color_status: "",
        answer: {
          active: true,
          message: "",
          files: [],
        },
        delegate: {
          active: false,
          departament: null,
          delegation: null,
          agents: [],
        },
        is_urgent: false,
        lead: false,
      },
      timeline: {
        loading: false,
        empty: false,
        data: [],
      },
    };
  },
  methods: {
    resetDelegateForm(close = false) {
      if (close) {
        this.ticket.delegate.active = false;
      }

      this.ticket.delegate.departament = null;
      this.ticket.delegate.delegation = null;
      this.ticket.delegate.agents = [];

      this.delegations.active = false;
      this.delegations.loading = false;
      this.delegations.data = [];

      this.agents.active = false;
      this.agents.loading = false;
      this.agents.data = [];
    },

    async openDelegate() {
      try {
        this.resetDelegateForm(false);

        this.loading = true;

        await this.fetchDepartaments();

        this.ticket.delegate.active = true;
      } catch (error) {
        console.log(error);
        alertFailed("No se pudieron cargar los datos para delegar.");
      } finally {
        this.loading = false;
      }
    },

    confirmUrgentTicket() {
      const isUrgent = this.ticket.is_urgent;

      this.$buefy.dialog.confirm({
        title: isUrgent ? "Quitar urgente" : "Marcar como urgente",
        message: isUrgent
          ? `¿Seguro que querés quitar la urgencia de la consulta #${this.ticketId(
              this.ticket.id
            )}?`
          : `¿Seguro que querés marcar la consulta #${this.ticketId(
              this.ticket.id
            )} como urgente?`,
        confirmText: isUrgent ? "Sí, quitar urgente" : "Sí, marcar urgente",
        cancelText: "Cancelar",
        type: "is-danger",
        hasIcon: true,
        iconPack: "fas",
        icon: "exclamation-circle",
        onConfirm: () => this.urgentTicket(),
      });
    },

    async onDepartamentSelect(selectedDepartament) {
      const departamentId = Number(selectedDepartament.id);

      this.ticket.delegate.departament = selectedDepartament;
      this.ticket.delegate.delegation = null;
      this.ticket.delegate.agents = [];

      this.agents.data = [];
      this.agents.active = false;

      this.delegations.data = [];
      this.delegations.active = false;

      if (departamentId === 10) {
        this.delegations.active = true;
        await this.fetchDelegations();
        return;
      }

      await this.fetchAgents(departamentId);
    },

    async onDelegationSelect(selectedDelegation) {
      this.ticket.delegate.delegation = selectedDelegation;
      this.ticket.delegate.agents = [];

      await this.fetchAgents(Number(selectedDelegation.id));
    },

    async fetchTicket(id) {
      try {
        //this.loading = true;
        const query = await this.$repositories.tickets.show(id);
        const COLOR_STATUS = {
          "not attended": "dark",
          open: "success",
          closed: "danger",
        };
        const COLOR_PRIORITY = {
          low: "success",
          medium: "warning",
          high: "danger",
        };
        const STATUS = {
          "not attended": "No atendido",
          open: "Abierto",
          closed: "Cerrado",
        };
        const ORIGIN = {
          whatsapp: {
            type: "is-success",
            pack: "fab",
            icon: "whatsapp",
            name: "Whatsapp",
          },
          messenger: {
            type: "is-info",
            pack: "fab",
            icon: "facebook-messenger",
            name: "Messenger",
          },
          instagram: {
            type: "is-light",
            pack: "fab",
            icon: "instagram",
            name: "Instagram",
          },
          web: {
            type: "is-light",
            pack: "fas",
            icon: "globe",
            name: "Web",
          },
          crm: {
            type: "is-light",
            pack: "fas",
            icon: "globe",
            name: "Crm",
          },
          email: {
            type: "is-light",
            pack: "far",
            icon: "envelope",
            name: "Email",
          },
          delegacion: {
            type: "is-light",
            pack: "fas",
            icon: "globe",
            name: "Delegación",
          },
          "call center": {
            type: "is-light",
            pack: "fas",
            icon: "globe",
            name: "Call Center",
          },
          referido: {
            type: "is-light",
            pack: "fas",
            icon: "globe",
            name: "Referido",
          },
        };
        const ticket = query.data.ticket;
        this.ticket.id = ticket.id;
        this.ticket.statuses.status = STATUS[ticket.status];
        this.ticket.is_urgent = ticket.is_urgent;
        this.ticket.contact.id = ticket.contact.id;
        this.ticket.contact.name = ticket.contact.fullname;
        this.ticket.contact.email = ticket.contact.email;
        this.ticket.contact.phone = ticket.contact.phone;
        this.ticket.contact.celphone = ticket.contact.celphone;
        this.ticket.contact.dni = ticket.contact.dni;
        this.ticket.contact.cuil = ticket.contact.cuil;
        this.ticket.contact.city = ticket.contact.city;
        this.ticket.contact.province = ticket.contact.province;
        this.ticket.departament.id = ticket.departament.id;
        this.ticket.departament.name = ticket.departament.name;
        this.ticket.agent.id = ticket.user ? ticket.user.id : undefined;
        this.ticket.agent.name = ticket.user ? ticket.user.name : "";
        this.ticket.agent.lastname = ticket.user ? ticket.user.lastname : "";
        this.ticket.agent.avatar = ticket.user ? ticket.user.avatar : null;
        this.ticket.origin = ORIGIN[ticket.origin];
        this.ticket.color_status = COLOR_STATUS[ticket.status];
        this.ticket.color_priority =
          ticket.status == "closed" ? "dark" : COLOR_PRIORITY[ticket.priority];
        this.ticket.lead = ticket.sale.length > 0 ? true : false;

        const messages = ticket.messages;

        this.ticket.messages = [];
        messages.forEach((message) => {
          const cleanMessage = message.content
            .replace(/<br\s*[\/]?>/gi, "\n")
            .replace(/<b>/gi, "")
            .replace(/<\/b>/gi, "\n");
          const ITEMS = {
            content: cleanMessage,
            date: message.created_at,
            type: message.type,
            user: message.user_name,
            files: [],
          };
          if (message.file.length > 0) {
            const files = message.file;
            files.forEach((file) => {
              ITEMS.files.push(file);
            });
          }
          this.ticket.messages.push(ITEMS);
        });

        const delegated = ticket.delegated;
        this.ticket.delegated = [];
        if (delegated.length > 0) {
          delegated.forEach((users) => {
            const ITEMS = {
              avatar: users.user.avatar,
              name: users.user.name,
              lastname: users.user.lastname,
            };
            this.ticket.delegated.push(ITEMS);
            this.ticket.delegate.agents.push(users.user.id);
          });
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchTimeLine() {
      try {
        this.timeline.loading = true;
        this.timeline.data = [];
        const id = this.$route.params.id;
        const query = await this.$repositories.tickets.timeline(id);
        if (query.data.timeline.length > 0) {
          query.data.timeline.forEach((item) => {
            const ITEMS = {
              id: item.id,
              content: item.content,
              user: item.user_name,
              date: item.created_at,
            };
            this.timeline.data.push(ITEMS);
          });
          this.timeline.empty = false;
        } else {
          this.timeline.empty = true;
        }
        this.timeline.loading = false;
        this.$store.commit("setTimeLine", true);
      } catch (error) {
        this.timeline.loading = false;
        this.timeline.empty = true;
      }
    },

    async fetchAgents(id) {
      try {
        this.agents.loading = true;
        this.agents.data = [];

        let query;

        if (this.delegations.active) {
          console.log("Endpoint usado: agents.delegations");
          query = await this.$repositories.agents.delegations(id);
        } else {
          console.log("Endpoint usado: agents.departament");
          query = await this.$repositories.agents.departament(id);
        }

        const users = query.data.users || [];
        const delegationPositionIds = [11, 1, 2, 3, 4, 6];
        const departamentPositionIds = [11, 1, 4, 6];
        const jefePosition = "JEFE/A";
        const isDelegation = this.delegations.active;

        const filteredUsers = users
          .filter((item) => {
            if (isDelegation) {
              return delegationPositionIds.includes(Number(item.position_id));
            }

            const position = this.normalizeText(item.position?.name);
            return (
              departamentPositionIds.includes(Number(item.position_id)) ||
              position === jefePosition
            );
          })
          .sort((a, b) => {
            const orderA = isDelegation
              ? delegationPositionIds.indexOf(Number(a.position_id))
              : this.getDepartamentPositionOrder(
                  Number(a.position_id),
                  a.position?.name
                );
            const orderB = isDelegation
              ? delegationPositionIds.indexOf(Number(b.position_id))
              : this.getDepartamentPositionOrder(
                  Number(b.position_id),
                  b.position?.name
                );

            if (orderA !== orderB) {
              return orderA - orderB;
            }

            const nameA = `${a.name} ${a.lastname}`;
            const nameB = `${b.name} ${b.lastname}`;

            return nameA.localeCompare(nameB);
          });

        console.log("Cantidad filtrada:", filteredUsers.length);

        this.agents.data = filteredUsers.map((item) => {
          return {
            id: item.id,
            name: `${item.name} ${item.lastname}`,
            position_id: Number(item.position_id),
            position: item.position?.name || "Sin cargo",
          };
        });
        this.agents.active = this.agents.data.length > 0;
      } catch (error) {
        console.log("ERROR fetchAgents:", error);
      } finally {
        this.agents.loading = false;
      }
    },

    async fetchDepartaments() {
      try {
        this.departaments.loading = true;
        this.departaments.disabled = true;
        this.departaments.data = [];
        const query = await this.$repositories.departaments.list_not_marketer();
        query.data.departaments.forEach((item) => {
          const items = {
            id: item.id,
            name: item.name,
          };
          this.departaments.data.push(items);
          this.departaments.disabled = false;
          this.departaments.loading = false;
        });
      } catch (error) {
        console.log(error);
      }
    },

    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();
    },

    getDepartamentPositionOrder(positionId, positionName) {
      if (positionId === 11) return 1;
      if (this.normalizeText(positionName) === "JEFE/A") return 2;
      if (positionId === 1) return 3;
      if (positionId === 4) return 4;
      if (positionId === 6) return 5;
      return 999;
    },

    async fetchDelegations() {
      try {
        this.delegations.loading = true;
        this.delegations.data = [];

        const query = await this.$repositories.delegations.list();

        const groups = {};

        query.data.delegations.forEach((item) => {
          if (Number(item.id) !== 114) {
            const typeKey = this.normalizeText(item.type);

            if (!groups[typeKey]) {
              groups[typeKey] = [];
            }

            groups[typeKey].push({
              id: item.id,
              name: item.name,
              type: typeKey,
            });
          }
        });

        const order = [
          "DELEGACION PROPIA",
          "DELEGACION TERCERIZADA",
          "CENTRO MEDICO",
          "BOCA DE EXPENDIO",
        ];

        this.delegations.data = order
          .map((type) => ({
            type,
            items: groups[type] || [],
          }))
          .filter((group) => group.items.length > 0);

        console.log("Delegaciones cargadas:", this.delegations.data);
      } catch (error) {
        console.log("Error cargando delegaciones:", error);
        this.delegations.data = [];
      } finally {
        this.delegations.loading = false;
      }
    },

    async delegateAgent() {
      try {
        if (this.ticket.delegate.agents.length <= 0) {
          alertFailed("Debe seleccionar un agente!");
          return;
        }

        const departamentId = Number(this.ticket.delegate.departament?.id);

        if (!departamentId || isNaN(departamentId)) {
          alertFailed("Debe seleccionar un departamento válido!");
          return;
        }

        if (departamentId === 10 && !this.ticket.delegate.delegation?.id) {
          alertFailed("Debe seleccionar una delegación!");
          return;
        }

        this.loading = true;

        const id = this.ticket.id;

        const data = new FormData();
        data.append("_method", "put");
        data.append("users", this.ticket.delegate.agents.join(","));
        data.append("departament", departamentId);

        await this.$repositories.tickets.delegate(id, data);

        this.resetDelegateForm(true);

        alertSuccess("Registro exitoso!");

        await this.fetchTicket(this.ticket.id);

        this.$EventBus.$emit("updatedTicket");
      } catch (error) {
        console.log(error);
        alertFailed("Ha ocurrido un error!");
      } finally {
        this.loading = false;
      }
    },
    async answerTicket() {
      try {
        if (
          this.ticket.answer.message != "" ||
          this.ticket.answer.files.length > 0
        ) {
          await this.sendMessage();
          await this.fetchTicket(this.ticket.id);
        }
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
      }
    },
    whatsappLink(celphone) {
      const number = String(celphone || "").replace(/\D/g, "");
      return `https://wa.me/${number}`;
    },
    messageAvatar(message) {
      const normalizedName = String(message.user || "")
        .trim()
        .toLocaleLowerCase();
      const candidates = [
        this.ticket.agent,
        ...this.ticket.delegated,
        this.$store.getters["getSession"],
      ].filter(Boolean);
      const matchingUser = candidates.find((user) => {
        const fullName = `${user.name || ""} ${user.lastname || ""}`
          .trim()
          .toLocaleLowerCase();
        return fullName && fullName === normalizedName;
      });

      return matchingUser && matchingUser.avatar
        ? matchingUser.avatar
        : this.$avatarDefault;
    },

    async urgentTicket() {
      try {
        this.loading = true;

        const ticket = this.ticket.id;
        const wasUrgent = this.ticket.is_urgent;

        const data = new FormData();
        data.append("_method", "put");

        await this.$repositories.tickets.urgent(ticket, data);

        if (wasUrgent) {
          alertInfo("Se ha quitado la urgencia!");
        } else {
          alertSuccess("Se ha marcado como urgente!");
        }

        await this.fetchTicket(ticket);
        this.$EventBus.$emit("updatedTicket");
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
      } finally {
        this.loading = false;
      }
    },

    async openTicket() {
      try {
        this.loading = true;
        const ticket = this.ticket.id;
        await this.$repositories.tickets.open(ticket);
        alertSuccess("Se ha abierto la consulta!");
        this.fetchTicket(ticket);
        this.$EventBus.$emit("updatedTicket");
        await this.sendWhatsAppTemplate();
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
        this.loading = false;
      }
    },
    async closeTicket() {
      try {
        this.loading = true;
        const ticket = this.ticket.id;
        await this.$repositories.tickets.close(ticket);
        alertInfo("Se ha cerrado la consulta!");
        this.fetchTicket(ticket);
        this.$EventBus.$emit("updatedTicket");
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
        this.loading = false;
      }
    },
    async sendMessage() {
      try {
        const id_user = this.$store.getters["getSession"]["id"];
        const user_name =
          this.$store.getters["getSession"]["name"] +
          " " +
          this.$store.getters["getSession"]["lastname"];
        const id = this.ticket.id;
        const data = new FormData();
        data.append("content", this.ticket.answer.message);
        data.append("type", "sended");
        data.append("user_id", id_user);
        data.append("user_name", user_name);
        this.ticket.answer.files.forEach((file) => {
          data.append("files[]", file);
        });
        await this.$repositories.tickets.message(id, data);
        //console.log('tipo de dato '+this.ticket.origin.name)
        if (this.ticket.origin.name == "Whatsapp") {
          await this.sendWhatsAppMessage();
        }

        this.ticket.answer.message = "";
        this.ticket.answer.files = [];
        alertSuccess("Mensaje enviado!");
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
        this.loading = false;
      }
      // console.log('numero '+this.ticket.contact.phone)
      //await this.sendWhatsAppMessage();
    },

    async sendWhatsAppMessage() {
      try {
        const response = await this.$repositories.tickets.sendMessage(
          this.ticket.contact.phone,
          this.ticket.answer.message
        );
        if (response.status === 200) {
          alertSuccess("Mensaje de WhatsApp enviado!");
        } else {
          alertFailed("Error al enviar mensaje de WhatsApp!");
        }
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
      }
    },

    async sendWhatsAppTemplate() {
      //console.log('numero '+this.ticket.contact.phone)
      const name =
        this.$store.getters["getSession"]["name"] +
        " " +
        this.$store.getters["getSession"]["lastname"];
      try {
        const response = await this.$repositories.tickets.sendMessageTemplate(
          this.ticket.contact.phone,
          name
        );
        if (response.status === 200) {
          alertSuccess("Mensaje de WhatsApp enviado!");
        } else {
          alertFailed("Error al enviar mensaje de WhatsApp!");
        }
      } catch (error) {
        alertFailed("Ha ocurrido un error!");
      }
    },

    async createLead() {
      try {
        this.loading = true;
        const data = new FormData();
        data.append("contact", this.ticket.contact.id);
        data.append("ticket", this.ticket.id);
        data.append("ticket_status", this.ticket.statuses.id);
        data.append("user_id", 0);
        data.append("agent", 0);
        data.append(
          "inherited_origin",
          `crm-consultas-${this.ticket.origin.name.toLowerCase()}`
        );
        data.append("origin", "crm-consultas");
        data.append("stage", 1); // Lead
        await this.$repositories.sales.create(data);
        this.loading = false;
        this.$router.push("/ventas");
        alertSuccess("Registro exitoso!");
      } catch (error) {
        this.loading = false;
        alertFailed("Ha ocurrido un error!");
      }
    },
    ticketId(value) {
      return fillString(value, 4);
    },
    removeFile(index) {
      this.ticket.answer.files.splice(index, 1);
    },
    urlShort(value) {
      const short = value.split("/");
      return short[8] ? short[8] : short[7];
    },
  },
  watch: {
    "$route.params.id": async function () {
      if (this.$route.params.id) {
        await this.fetchTicket(this.$route.params.id);
        await this.fetchDepartaments();
        await this.fetchDelegations();

        this.$store.commit("setToggle", true);
      } else {
        this.$store.commit("setToggle", false);
      }
    },
  },
  computed: {
    ...mapState(["toggle"]),
    contactLocation() {
      return [this.ticket.contact.city, this.ticket.contact.province]
        .filter(Boolean)
        .join(", ");
    },
  },
  async created() {
    if (this.$route.params.id) {
      await this.fetchTicket(this.$route.params.id);
      await this.fetchDepartaments();
      await this.fetchDelegations();
      this.$store.commit("setToggle", true);
    } else {
      this.$store.commit("setToggle", false);
    }
  },

  mounted() {
    this.messagePolling = setInterval(() => {
      const id = this.$route.params.id;
      if (id) {
        this.fetchTicket(id);
      }
    }, 30000); // cada 30 segundos
  },
  beforeDestroy() {
    clearInterval(this.messagePolling);
  },
};
</script>

<style lang="scss" scoped>
#ticket {
  height: 100dvh;
  box-sizing: border-box;
  padding-top: 1.5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  overflow: hidden;

  .ticket-content {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .level {
    .level-right {
      text-align: center;

      button {
        &.is-danger {
          opacity: 0.5;
        }

        &.active {
          opacity: 1 !important;
        }
      }
    }
  }

  .delegate {
    padding-top: 1rem;
    padding-bottom: 1rem;
    border-top: 2px solid $light;

    .departaments {
      padding-bottom: 1rem;
    }

    .agents {
      padding-top: 1rem;
      border-top: 1px solid $light;
    }

    ul {
      max-height: 320px;
      overflow-y: auto;
    }
  }

  .ticket-contact-pills {
    display: flex;
    flex-wrap: wrap;
    min-width: 0;
    max-width: 100%;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .level-left.is-block {
    flex: 1 1 0;
    min-width: 0;
  }

  .ticket-contact-collapse {
    display: block;
    width: 100%;
    min-width: 0;
  }

  .ticket-contact-trigger {
    flex-wrap: wrap;
    min-width: 0;
  }

  .ticket-contact-pill {
    display: inline-flex;
    align-items: center;
    flex: 0 1 auto;
    max-width: 100%;
    gap: 0.35rem;
    min-height: 28px;
    padding: 0.35rem 0.6rem;
    border-radius: 99px;
    background: #f0f5f6;
    color: #52616b;
    font-size: 0.78rem;
    line-height: 1.1;
    text-decoration: none;
    white-space: normal;
    overflow-wrap: anywhere;

    i {
      color: #02758f;
    }

    &[href]:hover {
      background: #e2f2f6;
      color: #02758f;
    }

    &--primary {
      background: #02758f;
      color: #ffffff;
      font-weight: 600;

      i {
        color: #ffffff;
      }

      &[href]:hover {
        background: #016177;
        color: #ffffff;
      }
    }
  }

  .ticket-status-mobile {
    display: none;
  }

  /*
    El segundo .columns es el cuerpo:
    - mensajes
    - o sección de delegar
  */
  .ticket-content > .columns:nth-of-type(2) {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .ticket-content > .columns:nth-of-type(2) > .column {
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .messages {
    flex: 1;
    min-height: 0;
    padding-top: 1rem;
    padding-bottom: 1rem;
    border-top: 2px solid $light;
    overflow-y: auto;

    ul {
      max-height: none;
      overflow: visible;
      padding-right: 0.5rem;
    }

    .message-box {
      display: flex;
      align-items: flex-end;
      margin-bottom: 1rem;
      max-width: 100%;

      .message {
        max-width: 100%;
        overflow: hidden;
        border-radius: 10px !important;

        .message-body {
          border-radius: 10px !important;
          border-color: transparent !important;
        }
      }

      &.me {
        .message {
          background: linear-gradient(
            45deg,
            rgba(0, 147, 191, 1) 35%,
            rgba(0, 117, 143, 1) 100%
          );

          .message-body {
            color: $white;
            background: transparent;
          }
        }
      }
    }
  }

  .custom-message-content pre {
    white-space: pre-wrap !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    font-family: GothamRounded, sans-serif;
    border-radius: 8px;
  }

  .navbar {
    display: block;
    position: relative;
    flex-shrink: 0;
    padding-bottom: 1rem;
    border-top: 1px solid $light;
    background: $white;

    .field {
      &.has-addons {
        border-bottom: 1px solid $light;
        padding: 1rem;

        input {
          border: none;
        }

        .control {
          .buttons {
            margin-top: 0;
          }
        }
      }
    }

    .buttons {
      margin-top: 1rem;
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 100%;
    padding: 0.75rem 0.75rem 1rem;
    overflow: visible;

    .ticket-content {
      height: auto;
      min-height: 100%;
    }

    .ticket-heading-row {
      display: block;
      position: relative;
      width: 100%;

      > .level-left {
        display: block;
        width: 100%;
      }

      > .level-left > .level-item:first-child {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        min-height: 38px;
        padding-right: 3.25rem;
        margin-bottom: 0.35rem !important;

        .title {
          flex-shrink: 0;
          margin: 0 !important;
        }

        .ticket-status-mobile {
          display: inline-flex;
          margin-left: auto;
          font-size: 0.68rem;
          transform: translateY(-2px);
        }
      }

      > .ticket-header-actions {
        position: absolute;
        top: 42px;
        right: 0;
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 0.35rem;
        margin: 0;

        .buttons {
          flex-wrap: nowrap;
        }

        .ticket-status-desktop {
          display: none;
        }
      }
    }

    .ticket-meta-row {
      display: flex !important;
      align-items: center;
      width: 100%;
      min-height: 44px;
      padding-right: 7rem;
      margin-bottom: 0.5rem;

      > .level-left {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        min-width: 0;
      }

      .level-item {
        flex-shrink: 0;
        margin-right: 0.35rem;
      }

      .media,
      .image {
        transform: translateY(-2px);
      }
    }

    .ticket-contact-trigger {
      align-items: center;
      justify-content: space-between;
      flex-wrap: nowrap;
      width: 100%;
      min-height: 44px;

      .title {
        min-width: 0;
        margin-right: 0.75rem !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      a {
        flex-shrink: 0;
      }
    }

    .ticket-content > .columns:nth-of-type(2) {
      display: block;
      overflow: visible;
    }

    .ticket-content > .columns:nth-of-type(2) > .column {
      display: block;
    }

    .ticket-contact-trigger .title,
    .level-item .title {
      font-size: 1.05rem;
    }

    .delegate {
      padding: 1rem;
      background: #f8fbfc;
      border: 1px solid #dcecef;
      border-radius: 12px;

      ul {
        max-height: 40vh;
      }

      .button {
        width: 100%;
      }
    }

    .messages {
      max-height: none;
      overflow: visible;

      ul {
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
        padding-right: 0;
      }

      .message-box {
        width: 100%;

        .message {
          min-width: 0;
          max-width: calc(100% - 44px);
        }

        .avatar .image {
          width: 32px;
          height: 32px;
          margin-right: 0.5rem !important;
        }
      }
    }

    .navbar {
      position: sticky;
      bottom: 0;
      z-index: 5;
      padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));
    }
  }
}
</style>
